import type { DirectoryOffer, DirectoryRelease } from './aggregate-releases'
import { packOffer, unpackOffer, type PackedDirectoryOffer } from './packed-offer'
import type { ProductPacking } from './packed-product'
import { packRelease, unpackRelease, type PackedDirectoryRelease } from './packed-release'
import { packingPool } from './packing-pool'
import type { ProductSpecs } from './release-specs'

export type { PackedDirectoryRelease } from './packed-release'

// The directory's client payload. The rows filter in memory over every family
// and every product, so the whole set crosses the server/client boundary — but
// facets, offers, specs and asset paths repeat across models. Intern those values,
// pack products/offers/facets into tuples, and strip shared asset URL prefixes.
// Unpacking restores the exact DirectoryRelease shape, so filtering, sorting
// and picking keep the complete data. See the Oct 1 consumption decision doc.

export interface PackedDirectory {
  /** Every distinct facet / seller string, referenced by index. */
  vocab: string[]
  lists: number[][]
  offers: PackedDirectoryOffer[]
  specs: ProductSpecs[]
  images: (string | null)[]
  audio: (string | null)[]
  /** The leading part shared by every image / audio URL, restored on unpack. */
  imagePrefix: string
  audioPrefix: string
  releases: PackedDirectoryRelease[]
}

const commonPrefix = (values: string[]): string => {
  if (values.length < 2) {
    return ''
  }
  let prefix = values[0]
  for (const value of values) {
    while (!value.startsWith(prefix)) {
      prefix = prefix.slice(0, -1)
    }
  }
  return prefix
}

const isString = (value: string | null): value is string => value != null

const stripPrefix = (value: string | null, prefix: string): string | null =>
  value != null && value.startsWith(prefix) ? value.slice(prefix.length) : value

const restorePrefix = (value: string | null, prefix: string): string | null => (value == null ? null : prefix + value)

const createPacking = (imagePrefix: string, audioPrefix: string) => {
  const words = packingPool<string>()
  const lists = packingPool<number[]>()
  const offers = packingPool<PackedDirectoryOffer>()
  const specs = packingPool<ProductSpecs>()
  const images = packingPool<string | null>()
  const audio = packingPool<string | null>()
  const pack: ProductPacking = {
    word: words.intern,
    list: (value) => lists.intern(value.map(words.intern)),
    offer: (value) => (value === null ? -1 : offers.intern(packOffer(value, words.intern))),
    specs: specs.intern,
    image: (value) => images.intern(stripPrefix(value, imagePrefix)),
    audio: (value) => audio.intern(stripPrefix(value, audioPrefix)),
  }

  return {
    pack,
    vocab: words.values,
    lists: lists.values,
    offers: offers.values,
    specs: specs.values,
    images: images.values,
    audio: audio.values,
  }
}

export const packReleases = (releases: DirectoryRelease[]): PackedDirectory => {
  const products = releases.flatMap((release) => release.products)
  const imagePrefix = commonPrefix([...releases, ...products].map((item) => item.image).filter(isString))
  const audioPrefix = commonPrefix([...releases, ...products].map((item) => item.quietAudioUrl).filter(isString))
  const { pack, ...tables } = createPacking(imagePrefix, audioPrefix)
  return {
    ...tables,
    imagePrefix,
    audioPrefix,
    releases: releases.map((release) => packRelease(release, pack)),
  }
}

export const unpackReleases = ({
  vocab,
  lists,
  offers,
  specs,
  images,
  audio,
  imagePrefix,
  audioPrefix,
  releases,
}: PackedDirectory): DirectoryRelease[] => {
  const word = (index: number): string => vocab[index] ?? ''
  const list = (index: number): string[] => lists[index].map(word)
  const offer = (index: number): DirectoryOffer | null => (offers[index] ? unpackOffer(offers[index], word) : null)
  const unpack = {
    word,
    list,
    offer,
    specs: (index: number) => specs[index],
    image: (index: number) => restorePrefix(images[index], imagePrefix),
    audio: (index: number) => restorePrefix(audio[index], audioPrefix),
  }
  return releases.map((release) => unpackRelease(release, unpack))
}
